import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, UserCog, ShieldCheck, Plus, Trash2, KeyRound, Search, X,
} from "lucide-react";
import { sr, auth } from "../../lib/sr";

const ROLES = ["client_admin", "user", "campaign_manager", "compliance_reviewer", "admin", "super_admin"];

export default function AdminUsers() {
  const me = auth.getUser();
  const isSuper = me?.role === "super_admin";
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client_admin" });
  const [busy, setBusy] = useState(false);

  const load = () => sr.adminUsers().then(setUsers).catch(() => setUsers({ error: true }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.includes("@") || form.password.length < 8) {
      toast.error("Name, valid email and 8+ char password required"); return;
    }
    setBusy(true);
    try {
      await sr.adminCreateUser(form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "client_admin" });
      setAdding(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create user"); }
    finally { setBusy(false); }
  };

  const changeRole = async (u, role) => {
    try { await sr.adminUpdateUser(u.id, { role }); toast.success(`Role updated to ${role.replace(/_/g, " ")}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 chars):`);
    if (!pw) return;
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try { await sr.adminUpdateUser(u.id, { password: pw }); toast.success("Password reset"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await sr.adminDeleteUser(u.id); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete user"); }
  };

  if (!users) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (users.error) return <div className="sr-card"><p className="sr-muted">Could not load users. Admin access is required.</p></div>;

  const rows = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-users-page">
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Users</span>
          <h1 style={{ fontSize: 30 }}>Users &amp; access</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Manage every account across all clients — roles, passwords and access.</p>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setAdding(!adding)} data-testid="admin-add-user-btn">
          {adding ? <><X size={15} /> Cancel</> : <><Plus size={15} /> Add user</>}
        </button>
      </div>

      {adding && (
        <div className="sr-card" style={{ marginBottom: 16 }} data-testid="admin-add-user-form">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div className="sr-field"><label className="sr-label">Name</label>
              <input className="sr-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="sr-field"><label className="sr-label">Email</label>
              <input className="sr-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="sr-field"><label className="sr-label">Temporary password</label>
              <input className="sr-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
            <div className="sr-field"><label className="sr-label">Role</label>
              <select className="sr-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
                {ROLES.filter((r) => r !== "super_admin" || isSuper).map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select></div>
          </div>
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }} onClick={create} disabled={busy} data-testid="new-user-submit">
            {busy ? <Loader2 size={15} className="sr-spin" /> : <><Plus size={15} /> Create user</>}
          </button>
        </div>
      )}

      <div className="sr-card" style={{ marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
        <Search size={16} style={{ color: "var(--muted)" }} />
        <input className="sr-input" placeholder="Search users by name or email" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" style={{ border: "none" }} />
        <span className="sr-badge sr-badge-gray">{users.length} users</span>
      </div>

      <div className="sr-card" style={{ overflowX: "auto" }}>
        <table className="sr-table" data-testid="admin-users-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th className="center">Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => {
              const locked = u.role === "super_admin" && !isSuper;
              return (
                <tr key={u.id} data-testid={`admin-user-${u.id}`}>
                  <td>{u.name || "—"}{u.is_demo && <span className="sr-badge sr-badge-amber" style={{ marginLeft: 6 }}>demo</span>}</td>
                  <td>{u.email}</td>
                  <td>
                    <select className="sr-select" value={u.role} disabled={locked}
                      onChange={(e) => changeRole(u, e.target.value)} data-testid={`role-select-${u.id}`}
                      style={{ padding: "4px 8px", fontSize: 13 }}>
                      {ROLES.filter((r) => r !== "super_admin" || isSuper || u.role === "super_admin").map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                    </select>
                  </td>
                  <td className="center">
                    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => resetPassword(u)} disabled={locked} data-testid={`reset-pw-${u.id}`} title="Reset password">
                        <KeyRound size={14} />
                      </button>
                      {isSuper && u.id !== me?.id && (
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} title="Delete user"
                          style={{ color: "var(--danger, #C0362C)" }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isSuper && <p className="sr-muted" style={{ fontSize: 12, marginTop: 12 }}><UserCog size={11} /> Only a super admin can delete accounts or manage other super admins.</p>}
    </div>
  );
}
